/**
 * useSaleRecords — query + mutation hooks for Bird Sale Records.
 *
 * Posting a sale automatically creates a population-OUT movement in the
 * Bird Movement Ledger (handled in birdRecordsApi.ts), so the mutation
 * invalidates both sale records and flock population caches.
 *
 * Hooks exported:
 *   useSaleRecords(filters?)          — list query with optional filters
 *   useCreateSaleRecord()             — create a new sale (draft)
 *   useUpdateSaleRecord()             — patch a sale (status transitions, etc.)
 *   useDeleteSaleRecord()             — remove a draft/cancelled sale
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query';
import {
  fetchSaleRecords,
  createSaleRecord,
  updateSaleRecord,
  deleteSaleRecord,
  type SaleRecordsFilter,
  type BirdSaleRecord,
} from '@/lib/birdRecordsApi';
import { queryKeys } from '@/lib/queryKeys';

// ─── List query ───────────────────────────────────────────────────────────────

export function useSaleRecords(
  filters?: SaleRecordsFilter,
  options?: Omit<UseQueryOptions<BirdSaleRecord[]>, 'queryKey' | 'queryFn'>,
) {
  return useQuery<BirdSaleRecord[]>({
    queryKey: queryKeys.saleRecords.list(filters as Record<string, unknown>),
    queryFn: () => fetchSaleRecords(filters),
    ...options,
  });
}

// ─── Create mutation ──────────────────────────────────────────────────────────

export function useCreateSaleRecord() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Omit<BirdSaleRecord, 'id' | 'saleNumber' | 'createdAt'>) =>
      createSaleRecord(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.saleRecords.all });
    },
  });
}

// ─── Update mutation (status transitions + field patches) ────────────────────

export function useUpdateSaleRecord() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Partial<BirdSaleRecord> }) =>
      updateSaleRecord(id, patch),

    // Optimistic update — immediately reflect status / field changes
    onMutate: async ({ id, patch }) => {
      await qc.cancelQueries({ queryKey: queryKeys.saleRecords.all });
      const previousData = qc.getQueriesData<BirdSaleRecord[]>({
        queryKey: queryKeys.saleRecords.all,
      });
      qc.setQueriesData<BirdSaleRecord[]>(
        { queryKey: queryKeys.saleRecords.all },
        (old) => old?.map((s) => (s.id === id ? { ...s, ...patch } : s)),
      );
      return { previousData };
    },
    onError: (_err, _vars, context) => {
      if (context?.previousData) {
        context.previousData.forEach(([queryKey, data]) => {
          qc.setQueryData(queryKey, data);
        });
      }
    },
    onSettled: (_data, _err, { patch }) => {
      qc.invalidateQueries({ queryKey: queryKeys.saleRecords.all });
      // If the sale was posted, population changed — invalidate both
      if (patch.status === 'posted') {
        qc.invalidateQueries({ queryKey: queryKeys.flockPopulation.all });
        qc.invalidateQueries({ queryKey: queryKeys.movements.all });
      }
    },
  });
}

// ─── Delete mutation ──────────────────────────────────────────────────────────

export function useDeleteSaleRecord() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteSaleRecord(id),
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: queryKeys.saleRecords.all });
      const previousData = qc.getQueriesData<BirdSaleRecord[]>({
        queryKey: queryKeys.saleRecords.all,
      });
      qc.setQueriesData<BirdSaleRecord[]>(
        { queryKey: queryKeys.saleRecords.all },
        (old) => old?.filter((s) => s.id !== id),
      );
      return { previousData };
    },
    onError: (_err, _vars, context) => {
      if (context?.previousData) {
        context.previousData.forEach(([queryKey, data]) => {
          qc.setQueryData(queryKey, data);
        });
      }
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: queryKeys.saleRecords.all });
    },
  });
}
